'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { bootstrapAccount } from '@/lib/auth/bootstrap'
import { trackEvent } from '@/lib/analytics/track'
import { BB_EVENTS } from '@/lib/analytics/events'

export default function LoginPage() {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deleted] = useState(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('deleted') === '1'
  })
  const [nextPath] = useState(() => {
    if (typeof window === 'undefined') return '/dashboard'
    const nextPathRaw = new URLSearchParams(window.location.search).get('next')
    if (!nextPathRaw) return '/dashboard'
    // Resolve the value the same way the browser will, then require it to stay
    // same-origin. This neutralises every off-site form of an open-redirect /
    // post-login phishing target: absolute URLs, protocol-relative ("//evil"),
    // backslash ("/\\evil"), and control-character ("/\tevil") tricks all
    // resolve to a foreign origin and get rejected. Only the normalised
    // same-origin path is returned.
    try {
      const resolved = new URL(nextPathRaw, window.location.origin)
      if (resolved.origin === window.location.origin) {
        return resolved.pathname + resolved.search + resolved.hash
      }
    } catch {
      // Malformed value — fall through to the safe default.
    }
    return '/dashboard'
  })

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Load the Supabase SDK on submit instead of at page load — keeps ~220KB
    // out of the login entry bundle so the form is interactive sooner.
    const { createClient } = await import('@/lib/supabase/client')
    const supabase = createClient()
    const { error } = await supabase.auth.signInWithPassword({ email, password })

    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    trackEvent(BB_EVENTS.LOGIN_COMPLETED)

    // Existing accounts already have org context, and the app layout can recover
    // missing setup records. Do not block the first app navigation on this extra
    // API round trip.
    void bootstrapAccount().catch(() => {})

    router.replace(nextPath)
    router.refresh()
  }

  return (
    <div className="bb-auth-card rounded-2xl p-8">
      <h1 className="text-white text-2xl font-bold mb-1">Welcome back</h1>
      <p className="text-slate-400 text-sm mb-6">Sign in to your BuilderBuddi account</p>

      {deleted && (
        <div className="mb-6 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          Your account has been deleted. Thanks for using BuilderBuddi.
        </div>
      )}

      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 text-sm"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-slate-300">Password</label>
            <Link href="/forgot-password" className="text-xs text-amber-400 hover:text-amber-300">
              Forgot password?
            </Link>
          </div>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            placeholder="••••••••"
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 text-sm"
          />
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bb-btn-primary disabled:bg-amber-500/50 text-slate-900 font-semibold py-3 rounded-xl transition-colors text-sm"
        >
          {loading ? 'Opening BuilderBuddi...' : 'Sign in'}
        </button>
      </form>

      <p className="text-center text-sm text-slate-400 mt-6">
        Don&apos;t have an account?{' '}
        <Link href="/signup" className="text-amber-400 hover:text-amber-300 font-medium">
          Sign up free
        </Link>
      </p>
    </div>
  )
}
